import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { analyticsConsent } from '../../lib/analyticsConsent';
import '../../styles/AnalyticsChoice.css';

const copy = {
  ko: {
    title: '방문 통계를 허용할까요?',
    body: 'Google Analytics로 웹 이용 통계를 분석하며, 관련 정보가 국외에서 처리됩니다. 거부해도 모든 기능을 이용할 수 있어요.',
    details: '자세히 보기',
    collapse: '접기',
    deny: '거부',
    allow: '허용',
    close: '나중에 선택',
    purpose: '목적: 웹 방문과 기능 이용 현황 분석',
    data: '항목: 쿠키 식별값, 기기·브라우저 정보, 페이지·콘텐츠 이름, 검색어·클릭 등 이용 기록, 로그인 회원의 내부 식별값과 클릭한 공개 프로필의 닉네임',
    recipient: '처리 업체: Google LLC(미국). 문의: googlekrsupport@google.com',
    transfer:
      '허용한 뒤 웹을 이용할 때 브라우저에서 암호화된 인터넷 통신으로 전송합니다. Google 및 하위 처리업체의 국외 시설에서 처리될 수 있습니다.',
    locations: 'Google 처리 시설·국가 안내',
    processors: '하위 처리업체 안내',
    period:
      '현재 이벤트 데이터는 2개월, 사용자 데이터는 14개월 보관하며 새 활동 시 사용자 보관기간이 다시 계산됩니다. 집계 보고서에는 이 기간이 동일하게 적용되지 않습니다.',
    change:
      '선택은 이 브라우저에 저장됩니다. 하단 ‘방문 통계 설정’에서 바꿀 수 있으며, 거부해도 로그인과 콘텐츠 이용에는 영향이 없습니다. 기존에 수집된 정보가 자동으로 삭제되는 것은 아닙니다.',
    policy: '개인정보처리방침 · 국외 처리 안내',
    error:
      '선택을 저장하지 못해 현재 페이지의 방문 통계를 중지했습니다. 브라우저 저장 설정을 확인해 주세요.',
  },
  en: {
    title: 'Allow website analytics?',
    body: 'We use Google Analytics to understand website usage. Related data is processed overseas. All features remain available if you decline.',
    details: 'Learn more',
    collapse: 'Show less',
    deny: 'Decline',
    allow: 'Allow',
    close: 'Decide later',
    purpose: 'Purpose: understand website visits and feature usage',
    data: 'Data: cookie identifiers, device/browser information, page/content names, search/click activity, signed-in members’ internal identifiers and nicknames of public profiles clicked',
    recipient: 'Provider: Google LLC (United States). Contact: googlekrsupport@google.com',
    transfer:
      'After you allow analytics, your browser sends data over encrypted internet connections while you use the website. Google and its subprocessors may process it at overseas facilities.',
    locations: 'Google facilities and countries',
    processors: 'Subprocessor information',
    period:
      'Event data is currently retained for 2 months and user data for 14 months, resetting user retention on new activity. These periods do not apply equally to aggregated reports.',
    change:
      'Your choice is saved in this browser and can be changed in Website analytics settings in the footer. Declining does not affect sign-in or content access, and does not automatically erase data already collected.',
    policy: 'Privacy policy · Overseas processing',
    error:
      'Your choice could not be saved, so analytics has been stopped on this page. Please check your browser storage settings.',
  },
};

export default function AnalyticsChoice({ open, onClose, locale = 'ko' }) {
  const choice = useSyncExternalStore(analyticsConsent.subscribe, analyticsConsent.getSnapshot);
  const [dismissed, setDismissed] = useState(false);
  const [details, setDetails] = useState(false);
  const [error, setError] = useState(false);
  const heading = useRef(null);
  const text = copy[locale === 'en' ? 'en' : 'ko'];
  useEffect(() => {
    if (open) heading.current?.focus();
  }, [open]);
  const close = () => {
    setError(false);
    setDismissed(true);
    onClose();
  };
  const choose = (next) => {
    if (!analyticsConsent.setChoice(next)) {
      setError(true);
      return;
    }
    setError(false);
    close();
  };
  if (!analyticsConsent.enabled || (!open && (dismissed || choice !== 'unknown') && !error))
    return null;
  return (
    <section
      className="pp-analytics-choice"
      aria-labelledby="analytics-choice-title"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          close();
        }
      }}
    >
      <button type="button" className="pp-analytics-close" aria-label={text.close} onClick={close}>
        ×
      </button>
      <h2 id="analytics-choice-title" ref={heading} tabIndex={-1}>
        {text.title}
      </h2>
      <p>{text.body}</p>
      {details && (
        <div id="analytics-choice-details" className="pp-analytics-details">
          <p>{text.purpose}</p>
          <p>{text.data}</p>
          <p>{text.period}</p>
          <p>{text.recipient}</p>
          <p>{text.transfer}</p>
          <p>
            <a
              href="https://www.google.com/about/datacenters/locations/"
              target="_blank"
              rel="noopener noreferrer"
            >
              {text.locations}
            </a>
            {' · '}
            <a
              href="https://business.safety.google/adssubprocessors/"
              target="_blank"
              rel="noopener noreferrer"
            >
              {text.processors}
            </a>
          </p>
          <p>{text.change}</p>
          <a href="/about/privacy-policy">{text.policy}</a>
        </div>
      )}
      {error && <p role="alert">{text.error}</p>}
      <div className="pp-analytics-actions">
        <button
          type="button"
          className="pp-analytics-details-link"
          aria-expanded={details}
          aria-controls="analytics-choice-details"
          onClick={() => setDetails(!details)}
        >
          {details ? text.collapse : text.details}
        </button>
        {details && (
          <button type="button" onClick={() => choose('denied')}>
            {text.deny}
          </button>
        )}
        <button type="button" onClick={() => choose('granted')}>
          {text.allow}
        </button>
      </div>
    </section>
  );
}
